import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../../../shared/api/axiosInstance';
import type {
  ProductPackaging,
  ProductPackagingCreate,
  ProductPackagingUpdate,
  ProductDefaultPackagings,
  Product,
} from '../types';
import { productKeys } from './useProducts';
import { productCatalogLayerKeys } from './productCatalogLayerKeys';

export const packagingKeys = {
  all: ['product-packagings'] as const,
  byProduct: (productId: string) => [...packagingKeys.all, productId] as const,
};

export function useProductPackagings(productId: string) {
  return useQuery({
    queryKey: packagingKeys.byProduct(productId),
    queryFn: async () => {
      const response = await axiosInstance.get<{ data: ProductPackaging[] }>(
        `/api/v1/master-data/products/products/${productId}/packagings`,
      );
      return response.data.data;
    },
    enabled: !!productId,
  });
}

export function useCreatePackaging() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      productId,
      data,
    }: {
      productId: string;
      data: ProductPackagingCreate;
    }) => {
      const response = await axiosInstance.post<{ data: ProductPackaging }>(
        `/api/v1/master-data/products/products/${productId}/packagings`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: packagingKeys.byProduct(productId) });
      queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) });
      // The batched catalog-layers read holds this product's layers too, and it
      // does not share a key prefix with the narrow key above.
      queryClient.invalidateQueries({ queryKey: productCatalogLayerKeys.all });
    },
  });
}

export function useUpdatePackaging() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      packagingId,
      productId: _productId,
      data,
    }: {
      packagingId: string;
      productId: string;
      data: ProductPackagingUpdate;
    }) => {
      const response = await axiosInstance.put<{ data: ProductPackaging }>(
        `/api/v1/master-data/products/packagings/${packagingId}`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: packagingKeys.byProduct(productId) });
      queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) });
      // The batched catalog-layers read holds this product's layers too, and it
      // does not share a key prefix with the narrow key above.
      queryClient.invalidateQueries({ queryKey: productCatalogLayerKeys.all });
    },
  });
}

export function useDeletePackaging() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      packagingId,
      productId: _productId,
    }: {
      packagingId: string;
      productId: string;
    }) => {
      await axiosInstance.delete(
        `/api/v1/master-data/products/packagings/${packagingId}`,
      );
    },
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: packagingKeys.byProduct(productId) });
      queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) });
      // The batched catalog-layers read holds this product's layers too, and it
      // does not share a key prefix with the narrow key above.
      queryClient.invalidateQueries({ queryKey: productCatalogLayerKeys.all });
    },
  });
}

export function useSetDefaultPackagings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      productId,
      data,
    }: {
      productId: string;
      data: ProductDefaultPackagings;
    }) => {
      const response = await axiosInstance.put<{ data: Product }>(
        `/api/v1/master-data/products/products/${productId}/default-packagings`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) });
      queryClient.invalidateQueries({ queryKey: packagingKeys.byProduct(productId) });
      // The batched catalog-layers read holds this product's layers too, and it
      // does not share a key prefix with the narrow key above.
      queryClient.invalidateQueries({ queryKey: productCatalogLayerKeys.all });
    },
  });
}
